<script lang="ts">
  // Card.svelte — uma carta individual. Trata 4 estados:
  //   1. Carta "back" (back-cover): nao tem value/suit (oponente
  //      escondida) ou explicitamente flipped+played.
  //   2. Carta normal aberta: mostra value + suit, colore vermelho
  //      pra copas/ouros.
  //   3. Manilha: destacada com glow neon.
  //   4. Playable: hover + cursor pointer quando o user pode clicar.
  import type { Card as CardType } from '../../stores/match';
  import { isRedSuit, nextValue } from '../../lib/cards';

  type Props = {
    card?: CardType | null;
    vira?: CardType | null;
    playable?: boolean;
    onClick?: () => void;
    size?: 'sm' | 'md' | 'lg';
  };
  let { card, vira, playable = false, onClick, size = 'md' }: Props = $props();

  let hasContent = $derived(!!(card?.value && card?.suit));
  let isBack = $derived(!hasContent || (card?.flipped && card?.played));
  let isRed = $derived(hasContent && isRedSuit(card?.suit));
  let manilhaVal = $derived(vira?.value ? nextValue(vira.value) : null);
  let isManilha = $derived(hasContent && !!manilhaVal && card?.value === manilhaVal);

  function handleClick() {
    if (!playable || !onClick) return;
    onClick();
  }
</script>

{#if isBack}
  <div class="card back size-{size}" aria-label="carta virada">
    <span class="back-mark">✦</span>
  </div>
{:else}
  <button
    type="button"
    class="card size-{size}"
    class:red={isRed}
    class:playable
    class:manilha={isManilha}
    disabled={!playable}
    onclick={handleClick}
    aria-label={`${card?.value}${card?.suit}`}
  >
    <span class="value top">{card?.value}</span>
    <span class="suit center">{card?.suit}</span>
    <span class="value bottom">{card?.value}</span>
  </button>
{/if}

<style>
  .card {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 5px 7px;
    background: linear-gradient(180deg, #f7f4ee, #d8d2c4);
    color: #1a0e2e;
    border: 1px solid rgba(180, 160, 255, 0.35);
    border-radius: 8px;
    font-weight: 700;
    user-select: none;
    box-shadow:
      0 3px 8px rgba(0, 0, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    transition: transform 0.15s, box-shadow 0.15s, border-color 0.2s;
    font-family: inherit;
    /* Override do button:disabled global (app.css opacity 0.4) — cartas
     * na mesa sao <button disabled> mas tem que aparecer com pleno
     * contraste. Felipe 2026-05-20. */
    opacity: 1;
  }
  button.card:disabled { opacity: 1; }
  /* Tamanhos responsivos: width via var, height = width * 1.5 (ratio
   * carta de baralho). Mantem proporcao em qualquer viewport. */
  .card.size-sm {
    width: var(--card-w-sm);
    height: calc(var(--card-w-sm) * 1.5);
    font-size: clamp(9px, 1.5vw, 11px);
  }
  .card.size-md {
    width: var(--card-w-md);
    height: calc(var(--card-w-md) * 1.5);
    font-size: clamp(11px, 2vw, 14px);
  }
  .card.size-lg {
    width: var(--card-w-lg);
    height: calc(var(--card-w-lg) * 1.5);
    font-size: clamp(13px, 2.5vw, 16px);
  }

  .card.red { color: #c41a4a; }

  .card .value { line-height: 1; }
  .card .value.top { align-self: flex-start; }
  .card .value.bottom { align-self: flex-end; transform: rotate(180deg); }
  .card .suit.center {
    align-self: center;
    font-size: 1.6em;
    line-height: 1;
  }

  .card.playable {
    cursor: pointer;
    box-shadow:
      0 3px 8px rgba(0, 0, 0, 0.5),
      0 0 14px rgba(108, 255, 189, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }
  .card.playable:hover {
    /* Override app.css global button:hover (que zerava o bg pra
     * var(--surface-hover) transparente). Felipe 2026-05-21. */
    background: linear-gradient(180deg, #fffaf0, #e6dfcf);
    border-color: rgba(180, 160, 255, 0.55);
    transform: translateY(-8px) scale(1.05);
    box-shadow:
      0 8px 18px rgba(0, 0, 0, 0.55),
      0 0 22px rgba(108, 255, 189, 0.75),
      inset 0 1px 0 rgba(255, 255, 255, 0.5);
  }
  .card.playable:active { transform: translateY(-4px) scale(1.02); }

  .card.manilha {
    border-color: rgba(255, 210, 74, 0.7);
    box-shadow:
      0 3px 10px rgba(0, 0, 0, 0.45),
      0 0 18px rgba(255, 210, 74, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }
  .card.manilha.playable:hover {
    box-shadow:
      0 8px 22px rgba(0, 0, 0, 0.55),
      0 0 26px rgba(255, 210, 74, 0.8),
      inset 0 1px 0 rgba(255, 255, 255, 0.5);
  }

  .card.back {
    /* Felipe 2026-05-20: backs estavam muito escuros — quase invisiveis
     * sobre o fundo. Roxo claro com mais contraste. */
    background: linear-gradient(145deg, #5b2b8c, #2a0e52);
    color: var(--cyan, #4ad3ff);
    border-color: rgba(255, 255, 255, 0.35);
    display: grid;
    place-items: center;
    overflow: hidden;
  }
  .card.back::before {
    content: '';
    position: absolute;
    inset: 6px;
    background:
      repeating-linear-gradient(45deg,
        rgba(255, 210, 74, 0.32) 0 6px,
        transparent 6px 12px);
    border-radius: 5px;
    opacity: 1;
    pointer-events: none;
  }
  .back-mark {
    position: relative;
    z-index: 1;
    font-size: 1.8em;
    color: #4ad3ff;
    text-shadow: 0 0 10px rgba(74, 211, 255, 0.85);
  }
</style>
